import { GradeChemistryLimit } from "@/pages/steel/models/CatalogGrade";
import { upsertCatGradeChemistryLimit } from "@/pages/steel/repositories/CatalogGradeRepository";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Grid2 as Grid, IconButton, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

type Props = {
	label?: String;
	element: GradeChemistryLimit;
	disabled: boolean;
	refreshData: Function;
	setError: (error: string) => void;
	setNotificationMessage: (message: string) => void;
};
export const useTextFieldStyles = makeStyles({
	input: {
		"& .MuiInputBase-root": {
			padding: "1px 0px 1px 2px !important",
		},
		"& .MuiOutlinedInput-input": {
			padding: "1px 0px 1px 4px !important",
			fontSize: "clamp(.7rem, 0.8rem, 1rem) !important",
		},
		"& label": {
			fontSize: "0.75rem !important",
		},
	},
});

export const ElementLimits = ({
	label = "C",
	element,
	disabled,
	refreshData,
	setError,
	setNotificationMessage,
}: Props) => {
	const [fieldsEnabled, setFieldEnabled] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const { control, handleSubmit, reset, watch } = useForm<GradeChemistryLimit>({
		mode: "onChange",
	});

	const classes = useTextFieldStyles();
	const onSubmit: SubmitHandler<GradeChemistryLimit> = async (data) => {
		setIsLoading(true);
		setError("");

		if (data.MinValue < 0 || data.MinValue > 100) {
			setError("MinValue must be between 0 and 100.");
			setIsLoading(false);
			return;
		}

		if (data.MaxValue < 0 || data.MaxValue > 100) {
			setError("MaxValue must be between 0 and 100.");
			setIsLoading(false);
			return;
		}

		if (data.MinValue > data.MaxValue) {
			setError("MinValue must be less than or equal to MaxValue.");
			setIsLoading(false);
			return;
		}

		if (data.AimValue !== null) {
			if (data.AimValue < data.MinValue || data.AimValue > data.MaxValue) {
				setError("AimValue must be between MinValue and MaxValue.");
				setIsLoading(false);
				return;
			}
		}

		const resp = await upsertCatGradeChemistryLimit({
			gradeId: data?.GradeId!,
			elementId: data.ElementId!,
			minValue: data.MinValue,
			maxValue: data.MaxValue,
			aimValue: data.AimValue,
		});
		setIsLoading(false);
		if (resp.ok) {
			setNotificationMessage("Chemistry limit saved successfully");
			await refreshData();
		} else {
			setError(resp.message!);
		}
		setFieldEnabled(false);
	};

	useEffect(() => {
		reset(element);
	}, [element]);

	return (
		<Grid container size={{ md: 12 }} spacing={1}>
			<Grid size={{ md: 4 }}>
				<Controller
					name="ElementName"
					control={control}
					render={() => (
						<TextField
							variant="outlined"
							value={element.ElementName}
							size="small"
							fullWidth
							disabled
							className={classes.input}
						/>
					)}
				/>
			</Grid>
			<Grid size={{ md: 2 }}>
				<Controller
					name="MinValue"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<TextField
							variant="outlined"
							value={field.value}
							onChange={field.onChange}
							size="small"
							fullWidth
							disabled={!fieldsEnabled}
							error={!!error}
							helperText={error?.message}
							className={classes.input}
						/>
					)}
				/>
			</Grid>
			<Grid size={{ md: 2 }}>
				<Controller
					name="MaxValue"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<TextField
							variant="outlined"
							value={field.value}
							onChange={field.onChange}
							size="small"
							fullWidth
							disabled={!fieldsEnabled}
							error={!!error}
							helperText={error?.message}
							className={classes.input}
						/>
					)}
				/>
			</Grid>
			<Grid size={{ md: 2 }}>
				<Controller
					name="AimValue"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<TextField
							variant="outlined"
							value={field.value}
							onChange={field.onChange}
							size="small"
							fullWidth
							disabled={!fieldsEnabled}
							error={!!error}
							helperText={error?.message}
							className={classes.input}
						/>
					)}
				/>
			</Grid>
			<Grid size={{ md: 2 }}>
				{!fieldsEnabled ? (
					<IconButton
						onClick={() => setFieldEnabled(true)}
						title={`Edit '${label}'`}
						color="primary"
						disabled={disabled}
						size="small"
					>
						<EditIcon style={{ width: 20, height: 20 }} />
					</IconButton>
				) : (
					<>
						<IconButton
							onClick={() => setFieldEnabled(false)}
							color="error"
							size="small"
						>
							<CloseIcon style={{ width: 20, height: 20 }} />
						</IconButton>
						<IconButton
							onClick={handleSubmit(onSubmit)}
							type="submit"
							size="small"
						>
							<CheckIcon style={{ width: 20, height: 20, color: "green" }} />
						</IconButton>
					</>
				)}
			</Grid>
		</Grid>
	);
};
